import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
// Bundled with its imports into one file, so the worker loads from app://hub on the Mac as well as over https.
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import type { MapPin } from "@hub/shared";
import { useTheme } from "@/app/theme";
import { clusterPoints, isSinglePlace, type Cluster } from "./cluster";

maplibregl.setWorkerUrl(workerUrl);

/** OpenFreeMap: free street maps from OpenStreetMap, no account or key. */
const STYLE_LIGHT = "https://tiles.openfreemap.org/styles/positron";
const STYLE_DARK = "https://tiles.openfreemap.org/styles/dark";
/** Pins closer than this many screen pixels share a cluster. */
const CLUSTER_PX = 34;
const WORLD: maplibregl.LngLatBoundsLike = [[-170, -58], [190, 75]];

export type MapFocus =
  | { key: number; latitude: number; longitude: number; zoom: number }
  | { key: number; world: true };

export interface StreetMapProps {
  pins: MapPin[];
  /** People to ring, from the list beside the map. */
  selectedIds: Set<string>;
  me: { latitude: number; longitude: number } | null;
  /** A new key moves the map there. */
  focus: MapFocus | null;
  /** A pin of one person opens them; a group of people is handed back to list them. */
  onOpen: (contactId: string) => void;
  onGroup: (pins: MapPin[], title: string) => void;
}

export function StreetMap({ pins, selectedIds, me, focus, onOpen, onGroup }: StreetMapProps) {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const meMarker = useRef<maplibregl.Marker | null>(null);
  const [view, setView] = useState(0);
  const [failed, setFailed] = useState<string | null>(null);
  const [tilesDown, setTilesDown] = useState(false);
  const dark = useTheme((s) => s.resolved !== "light");
  const latest = useRef({ onOpen, onGroup });
  latest.current = { onOpen, onGroup };

  useEffect(() => {
    if (!container.current) return;
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({ container: container.current, style: dark ? STYLE_DARK : STYLE_LIGHT, bounds: WORLD, attributionControl: { compact: true }, dragRotate: false, pitchWithRotate: false });
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
      return;
    }
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    // Markers are regrouped whenever the view settles, so the groups always match what is on screen.
    map.on("moveend", () => setView((v) => v + 1));
    map.on("load", () => setView((v) => v + 1));
    // Without the internet the tiles do not come, but the pins and the list still work.
    map.on("error", (e: maplibregl.ErrorEvent) => {
      const status = (e.error as { status?: number } | undefined)?.status;
      if (!status || status >= 500 || status === 0) setTilesDown(true);
    });
    map.on("data", (e: { dataType?: string }) => {
      if (e.dataType === "source") setTilesDown(false);
    });
    mapRef.current = map;
    return () => {
      markers.current = [];
      map.remove();
      mapRef.current = null;
    };
    // The style follows the theme below; the map itself is made once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mapRef.current?.setStyle(dark ? STYLE_DARK : STYLE_LIGHT);
  }, [dark]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    if ("world" in focus) map.fitBounds(WORLD, { duration: 600 });
    else map.flyTo({ center: [focus.longitude, focus.latitude], zoom: focus.zoom, duration: 800 });
  }, [focus?.key]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    meMarker.current?.remove();
    meMarker.current = null;
    if (!me) return;
    const el = document.createElement("div");
    el.title = "You are here";
    el.style.cssText = "width:14px;height:14px;border-radius:50%;background:var(--accent);border:2px solid var(--bg);box-shadow:0 0 0 8px color-mix(in srgb, var(--accent) 20%, transparent)";
    meMarker.current = new maplibregl.Marker({ element: el }).setLngLat([me.longitude, me.latitude]).addTo(map);
  }, [me]);

  // One marker per group of pins close together on screen at this zoom.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    for (const m of markers.current) m.remove();
    markers.current = [];
    const points = pins.map((p) => {
      const at = map.project([p.longitude, p.latitude]);
      return { x: at.x, y: at.y, item: p };
    });
    for (const c of clusterPoints(points, CLUSTER_PX, (p) => p.contactId)) {
      const el = pinElement(c, c.items.some((p) => selectedIds.has(p.contactId)));
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        tapped(map, c);
      });
      const lngLat = map.unproject([c.x, c.y]);
      // The bubble sits on the place; the name hangs below it.
      markers.current.push(new maplibregl.Marker({ element: el, anchor: "top", offset: [0, -bubbleSize(c.items.length) / 2] }).setLngLat(lngLat).addTo(map));
    }
  }, [pins, selectedIds, view]);

  const tapped = (map: maplibregl.Map, c: Cluster<MapPin>) => {
    if (c.items.length === 1) {
      latest.current.onOpen(c.items[0].contactId);
      return;
    }
    const cities = [...new Set(c.items.map((p) => p.city?.trim() || "Somewhere"))];
    latest.current.onGroup(c.items, cities.length === 1 ? cities[0] : `${c.items.length} people in ${cities.length} places`);
    if (isSinglePlace(c)) {
      map.flyTo({ center: map.unproject([c.x, c.y]), zoom: Math.max(map.getZoom() + 2, 11), duration: 600 });
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    for (const p of c.items) bounds.extend([p.longitude, p.latitude]);
    map.fitBounds(bounds, { padding: 80, maxZoom: 13, duration: 600 });
  };

  if (failed) {
    return (
      <div className="u-small u-muted" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        This computer cannot draw the map ({failed}). The places list works.
      </div>
    );
  }
  return (
    <>
      <div ref={container} style={{ position: "absolute", inset: 0 }} role="region" aria-label="Map of where your people are" />
      {tilesDown && (
        <div className="u-small" style={{ position: "absolute", left: 12, bottom: 12, padding: "6px 10px", borderRadius: "var(--radius-md)", background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
          The map needs the internet. Your people and the places list still work.
        </div>
      )}
    </>
  );
}

const bubbleSize = (n: number) => (n > 1 ? 26 + Math.min(18, Math.log2(n) * 4) : 28);

/** A pin as plain elements: maplibre owns its markers, so React stays out of them. */
function pinElement(cluster: Cluster<MapPin>, lit: boolean): HTMLDivElement {
  const n = cluster.items.length;
  const one = cluster.items[0];
  const size = bubbleSize(n);
  const label = n === 1 ? one.displayName : isSinglePlace(cluster) ? one.city ?? null : null;
  const el = document.createElement("div");
  el.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:pointer";
  el.title = `${isSinglePlace(cluster) && one.city ? `${one.city}: ` : ""}${cluster.items.map((p) => p.displayName).join(", ")}`;
  const bubble = document.createElement("div");
  bubble.style.cssText = [
    `width:${size}px`,
    `height:${size}px`,
    "border-radius:50%",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "overflow:hidden",
    `background:${n > 1 ? "var(--accent)" : "var(--tier-close)"}`,
    "color:var(--text-inverse)",
    "font-weight:700",
    `font-size:${n > 99 ? 11 : 12}px`,
    "border:2px solid var(--bg)",
    `box-shadow:${lit ? "0 0 0 2px var(--accent), var(--shadow-md)" : "var(--shadow-md)"}`,
  ].join(";");
  if (n === 1 && one.avatarUrl) {
    const img = document.createElement("img");
    img.src = one.avatarUrl;
    img.alt = "";
    img.style.cssText = "width:100%;height:100%;object-fit:cover";
    bubble.append(img);
  } else bubble.textContent = n > 1 ? String(n) : one.displayName.split(" ").map((p) => p[0]).join("").slice(0, 2);
  el.append(bubble);
  if (label) {
    const name = document.createElement("div");
    name.className = "u-small";
    name.textContent = label;
    name.style.cssText = "margin-top:2px;padding:0 4px;border-radius:4px;background:color-mix(in srgb, var(--bg) 80%, transparent);color:var(--text-secondary);white-space:nowrap;max-width:160px;overflow:hidden;text-overflow:ellipsis";
    el.append(name);
  }
  return el;
}
